import SiteNav from '../components/layout/SiteNav'
import { useEffect } from 'react'
import { Users, UtensilsCrossed, Hotel, Truck, Timer, Headphones, Building, Clock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function Platform() {
  const navigate = useNavigate()

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('visible')
      }),
      { threshold: 0.1 }
    )
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const products = [
    { Icon: Users,           iconColor: '#B8734A', name: 'Collective Staff',       desc: 'Complete HR portal for small businesses. Onboarding, policies, timesheets, performance, and more.',              status: 'live',    statusLabel: 'Live Now',          color: '#B8734A', url: 'https://collectivestaff.app' },
    { Icon: UtensilsCrossed, iconColor: '#4A7C59', name: 'Collective Dining',      desc: 'Restaurant & food service — shift scheduling, tip tracking, inventory, revenue vs labor, and food safety.',  status: 'soon',    statusLabel: 'Coming Soon',       color: '#4A7C59', url: null },
    { Icon: Hotel,           iconColor: '#6B7D6B', name: 'Collective Hospitality', desc: 'Hotel & lodging management — guest check-in/out, housekeeping schedules, room assignments, and front desk tools.',                            status: 'soon',    statusLabel: 'Coming Soon',       color: '#6B7D6B', url: null },
    { Icon: Truck,           iconColor: '#6B7D6B', name: 'Collective Fleet',       desc: 'Vehicle & transportation management — assignments, inspections, maintenance, and DOT compliance.',              status: 'planned', statusLabel: 'In Development',    color: '#6B7D6B', url: null },
    { Icon: Timer,           iconColor: '#D9C3A3', name: 'Collective Time',        desc: 'Timekeeping for retail & hourly workers — GPS clock-in, shift scheduling, and payroll export. Also available as a Collective Staff add-on.',  status: 'planned', statusLabel: 'In Development', color: '#D9C3A3', url: null },
  ]

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", background: '#F7F2EB', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Montserrat:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .reveal.visible { opacity: 1; transform: translateY(0); }
        .reveal-delay-1 { transition-delay: 0.1s; }
        .reveal-delay-2 { transition-delay: 0.2s; }
        .reveal-delay-3 { transition-delay: 0.3s; }
        .reveal-delay-4 { transition-delay: 0.4s; }
        .product-card { border-radius: 16px; padding: 1.75rem; transition: all 0.25s; position: relative; overflow: hidden; }
        .product-card.clickable:hover { transform: translateY(-4px); box-shadow: 0 8px 32px rgba(43,43,43,0.1); }
        .primary-btn { background: #B8734A; color: #fff; border: none; border-radius: 8px; padding: 12px 28px; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .primary-btn:hover { background: #C9895F; }
      `}</style>

      <SiteNav />

      {/* Hero */}
      <div style={{ background: '#2B2B2B', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div className="reveal" style={{ fontSize: 11, fontWeight: 600, color: '#B8734A', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.75rem' }}>The GCW Platform</div>
          <h1 className="reveal reveal-delay-1" style={{ fontFamily: 'Playfair Display, serif', fontSize: '3rem', fontWeight: 700, color: '#fff', marginBottom: '1rem' }}>One platform. Every business need.</h1>
          <p className="reveal reveal-delay-2" style={{ fontSize: 15, color: 'rgba(217,195,163,0.6)', lineHeight: 1.8 }}>
            Collective Staff is just the beginning. GCW is building a complete suite of business management tools — all sharing the same beautiful, simple design philosophy.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '4rem 2rem' }}>

        {/* Products grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: '2.5rem' }}>
          {products.map((p, i) => (
            <div key={p.name}
              className={`product-card reveal reveal-delay-${i + 1} ${p.url ? 'clickable' : ''}`}
              style={{ background: p.status === 'live' ? '#fff' : '#F0EBE2', border: p.status === 'live' ? '2px solid #B8734A' : '1px solid #EDE6DA', cursor: p.url ? 'pointer' : 'default' }}
              onClick={() => p.url && window.open(p.url, '_blank')}>
              <div style={{ width:52,height:52,borderRadius:12,background:p.iconColor+'18',border:'1px solid '+p.iconColor+'33',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'1.25rem' }}>
                <p.Icon size={26} color={p.iconColor} strokeWidth={1.75}/>
              </div>
              {p.status === 'live' && (
                <div style={{ position: 'absolute', top: 0, right: 0, background: '#B8734A', color: '#fff', fontSize: 10, fontWeight: 600, padding: '4px 12px', borderRadius: '0 16px 0 8px' }}>LIVE</div>
              )}
              <div style={{ fontSize: 32, marginBottom: '0.75rem' }}>{p.icon}</div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 16, fontWeight: 600, color: '#2B2B2B', marginBottom: '0.5rem' }}>{p.name}</div>
              <div style={{ fontSize: 12, color: '#6B7D6B', lineHeight: 1.7, marginBottom: '1rem' }}>{p.desc}</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: p.color + '22', color: p.color }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: p.color }} />
                {p.statusLabel}
              </div>
              {p.url && <div style={{ marginTop: '0.75rem', fontSize: 11, color: p.color }}>Click to visit →</div>}
            </div>
          ))}
        </div>

        {/* Collective Suite */}
        <div className="reveal" style={{ background: '#2B2B2B', borderRadius: 16, padding: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '3rem' }}>
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
              Collective Suite <span style={{ fontSize: 14, fontWeight: 400, color: 'rgba(217,195,163,0.5)', fontFamily: "'Montserrat', sans-serif" }}>— coming soon</span>
            </div>
            <div style={{ fontSize: 13, color: 'rgba(217,195,163,0.5)', lineHeight: 1.7, maxWidth: 520 }}>
              Bundle every GCW product into one enterprise subscription. One login, one bill, one platform — Staff, Hospitality, Fleet, Time, and every product we build next.
            </div>
          </div>
          <button className="primary-btn" style={{ whiteSpace: 'nowrap', fontSize: 14, padding: '12px 28px' }}
            onClick={() => navigate('/about')}>
            Join the Waitlist →
          </button>
        </div>

        {/* Why GCW */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { icon: '🏗️', title: 'Built on one foundation', desc: 'Every GCW product shares the same auth, database, and brand engine. Adding a new product takes days, not months.' },
            { icon: '🔒', title: 'White-labeled per client', desc: 'Each client sees their own logo, colors, and domain. It feels like their software — powered by GCW.' },
            { icon: '📈', title: 'Grows with your business', desc: 'Start with Staff, add Hospitality when you open a restaurant, add Fleet when you get vehicles. One platform, your whole business.' },
          ].map((item, i) => (
            <div key={item.title} className={`reveal reveal-delay-${i + 1}`} style={{ background: '#fff', border: '1px solid #EDE6DA', borderRadius: 12, padding: '1.5rem' }}>
              <div style={{ fontSize: 28, marginBottom: '0.75rem' }}>{item.icon}</div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 15, fontWeight: 600, color: '#2B2B2B', marginBottom: '0.5rem' }}>{item.title}</div>
              <div style={{ fontSize: 13, color: '#6B7D6B', lineHeight: 1.7 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}